import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiMutate } from "@/lib/api";

export interface ConversationSummary {
  id: string;
  title: string;
  model: string;
  message_count: number;
  is_starred: number;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  model: string | null;
  tool_calls: ToolCallInfo[] | null;
  tool_call_count: number;
  duration_ms: number | null;
  feedback_rating: number | null;
  feedback_text: string | null;
  feedback_categories: string[] | null;
  attachments_meta: string | null;
  created_at: string;
}

export interface ToolCallInfo {
  tool: string;
  input: Record<string, unknown>;
  output: unknown;
}

export interface ConversationDetail extends ConversationSummary {
  messages: ConversationMessage[];
}

interface SendMessageResponse {
  id: string;
  role: "assistant";
  content: string;
  model: string;
  tool_calls: ToolCallInfo[];
  tool_call_count: number;
  duration_ms: number;
  conversation_title?: string;
}

export function useConversations() {
  return useQuery({
    queryKey: ["conversations"],
    queryFn: () => apiFetch<ConversationSummary[]>("/api/conversations"),
  });
}

export function useConversation(id: string | null) {
  return useQuery({
    queryKey: ["conversation", id],
    queryFn: () => apiFetch<ConversationDetail>(`/api/conversations/${id}`),
    enabled: id !== null,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data?: { title?: string; model?: string }) =>
      apiMutate<{ id: string; title: string; model: string; message_count: number }>(
        "/api/conversations",
        "POST",
        data || {}
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiMutate<{ ok: boolean }>(`/api/conversations/${id}`, "DELETE"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

export function useRenameConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      apiMutate<{ ok: boolean }>(`/api/conversations/${id}`, "PUT", { title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

export function useStarConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_starred }: { id: string; is_starred: boolean }) =>
      apiMutate<{ ok: boolean }>(`/api/conversations/${id}`, "PUT", { is_starred }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      content,
      model,
    }: {
      conversationId: string;
      content: string;
      model?: string;
    }) =>
      apiMutate<SendMessageResponse>(
        `/api/conversations/${conversationId}/messages`,
        "POST",
        { content, model }
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["conversation", vars.conversationId] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      messageId,
      rating,
      text,
      categories,
    }: {
      conversationId: string;
      messageId: string;
      rating: number;
      text?: string;
      categories?: string[];
    }) =>
      apiMutate<{ ok: boolean }>(
        `/api/conversations/${conversationId}/messages/${messageId}/feedback`,
        "PUT",
        { rating, text, categories }
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["conversation", vars.conversationId] });
    },
  });
}

